import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { animated, useSpring } from "react-spring";
import { Box, ListItemText } from "@material-ui/core";
import TestRequestApi from "../../../api/services/TestRequestApi";
import SubstrateSamplesApi from "../../../api/services/SubstrateSamplesApi";
import { SubstrateSampleContext } from "../../../contexts/SubstrateSampleContext";
import TestRequestPermissionContext from "../../../contexts/TestRequestPermissionContext";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import useEnv from "../../../hooks/env";
import SubstrateSample from "../../../api/models/substrate/SubstrateSample";
import CustomButton from "../../../components/Button/CustomButton";
import { StyledMenu, StyledMenuItem } from "../../../components/Menu/StyledMenu";
import CustomProgress from "../../../components/Alert/CustomProgress";
import { GalleryContextProvider } from "../../../contexts/GalleryContext";
import { OTHER, PICTURE, TDS } from "../../../components/Gallery/model/GalleryFlag.model";

import SubstrateAddDialog from "./SubstrateAddDialog";
import SubstrateDetailsDialog from "./SubstrateDetailsDialog";
import SubstrateTable from "./SubstrateTable";
import ParameterListDialog from "../../../components/Dialog/ParameterListDialog/ParameterListDialog";
import CustomFooterGraphic from "../CustomFooterGraphic";
import { fadeIn } from "../../../styles/springs";
import SubstrateReuseDialog from "./SubstrateReuseDialog";
import RequestsPagePathParams from "../../../api/models/test-request/RequestsPagePathParams";
import { TestRequestTabType } from "../useTestRequestParams";
import useQueryParams from "../../../hooks/useQueryParams.hook";
import SampleSuggestionField from "../SampleSuggestionField";
import InfoBox from "../../../components/InfoBox";

export type DialogWithCallback = "detail" | "reuse" | "duplicate";

const ADD_SUBSTRATE_SAMPLE_INFO_TEXT = `
  * The Substrate Sample is the actual sample that you have in front of you that you will use for the Test Request.
  * Choose 'tesa Standard' if you want to test on a material which is defined by tesa and is commonly available in tesa
    labs (e.g. tesa steel or polycarbonate plate).
  * Choose 'Customer Standard + Non Standard' if you have any material which is defined by a customer and commonly
    available in your lab OR if you receive any undefined material / something you just ordered for this test.
  * Choose 'Customer Part' if you have a specific part for testing like a car bumper or a door or a specific smartphone
    frame.
  * Choose 'Use Existing Substrate Sample' if...
  * Choose 'Duplicate Existing Substrate Sample' if...
  * If you have several plates of the exact same material and you want to use them as the same material you can just
    create one Substrate Sample for all of them.
`;

const SubstrateTab: React.FunctionComponent = () => {
  const { api } = useEnv();
  const history = useHistory();
  const springProps = useSpring(fadeIn);
  const testRequestApi: TestRequestApi = new TestRequestApi(api);
  const substrateSamplesApi: SubstrateSamplesApi = new SubstrateSamplesApi(api);
  const { testRequestId, dialog } = useParams<RequestsPagePathParams>();
  const queryParams = useQueryParams();
  const [isLoading, setIsLoading] = useState(true);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isReuseDialogOpen, setIsReuseDialogOpen] = useState(false);
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false);
  const [preSelectedProductType, setPreSelectedProductType] = useState<string | null>(null);
  const [sampleDetailsLoading, setSampleDetailsLoading] = useState(false);
  const [substrateSamples, setSubstrateSamples] = useState<SubstrateSample[]>([]);
  const {
    testRequest,
    updateTestRequest,
    parameterListDialog,
    substrateContext,
    updateSubstrateContext,
    updateSubstrateClasses,
    updateSubstrateId,
    updateSubstrateObject,
  } = useContext(SubstrateSampleContext);
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const { accessSubstrateSamples, newSubstrateSample, updatePermissionState } =
    useContext(TestRequestPermissionContext);
  const handleSampleEdit = (sampleId: number) => {
    setSampleDetailsLoading(true);
    substrateSamplesApi
      .fetchSubstrateSampleById(testRequestId, sampleId)
      .then(({ data }) => {
        const { substrateType } = data;
        if (substrateType) {
          updateSubstrateId(sampleId);
          updateSubstrateContext([substrateType]);
          updateSubstrateObject(data);
          setIsDetailDialogOpen(true);
        }
      })
      .catch(handleHttpError)
      .finally(() => setSampleDetailsLoading(false));
  };

  useEffect(() => {
    // remove postfixes from current path
    history.push(`/requests/${testRequestId}/${TestRequestTabType.SUBSTRATE}`);
  }, []);

  useEffect(() => {
    if (dialog === "addSubstrateSample" && substrateContext.length > 0) {
      setIsAddDialogOpen(true);
      setPreSelectedProductType(queryParams.get("type"));
    }
  }, [dialog, substrateContext]);

  const getTestRequest = (): void => {
    if (testRequestId !== "new") {
      testRequestApi
        .getTestRequest(testRequestId)
        .then((resp) => {
          updateTestRequest(resp.data);
          const { substrateSampleReferences } = resp.data;
          if (substrateSampleReferences && substrateSampleReferences.length) {
            return substrateSamplesApi
              .getSelectedSubstrateSamples(testRequestId)
              .then((innerResp) => {
                setSubstrateSamples(innerResp);
              })
              .catch(handleHttpError);
          }
          setSubstrateSamples([]);
        })
        .catch(handleHttpError)
        .finally(() => setIsLoading(false));
    }
  };

  const callbackUpdate = (closeDialog: boolean, dialogWithCallback: DialogWithCallback = "detail"): void => {
    updatePermissionState().then(getTestRequest).catch(handleHttpError);
    if (closeDialog) {
      switch (dialogWithCallback) {
        case "detail":
          setIsDetailDialogOpen(false);
          break;
        case "reuse":
          setIsReuseDialogOpen(false);
          break;
        case "duplicate":
          setIsCloneDialogOpen(false);
          break;
        default:
        // do nothing
      }
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  useEffect(() => {
    getTestRequest();
  }, []);

  if (!accessSubstrateSamples) {
    return <animated.div style={springProps}>Access denied.</animated.div>;
  }

  if (isLoading) {
    return <CustomProgress />;
  }

  return (
    <animated.div style={springProps}>
      <Box mb={3}>
        <SampleSuggestionField sampleTypeName="Substrate" value={testRequest?.description?.substrates || null} />
      </Box>
      <Box>
        {newSubstrateSample ? (
          <InfoBox compress text={ADD_SUBSTRATE_SAMPLE_INFO_TEXT}>
            <CustomButton add onClick={handleClick} text="Add Substrate Sample" />
          </InfoBox>
        ) : null}
        {substrateSamples.length > 0 ? (
          <SubstrateTable
            onEdit={handleSampleEdit}
            callbackUpdate={callbackUpdate}
            substrateSamples={substrateSamples}
          />
        ) : (
          <></>
        )}
        <SubstrateAddDialog
          onClose={(): void => {
            setIsAddDialogOpen(false);
          }}
          onSave={(): void => {
            setIsAddDialogOpen(false);
            setIsDetailDialogOpen(true);
          }}
          open={isAddDialogOpen}
          preSelectedProductType={preSelectedProductType}
        />
        <GalleryContextProvider
          flags={[
            PICTURE,
            TDS,
            {
              key: "IR",
              type: "IR",
              order: 2,
              label: "IR",
              longLabel: "IR SPECTRA",
              tags: ["IR"],
            },
            OTHER,
          ]}
        >
          <SubstrateDetailsDialog
            callback={callbackUpdate}
            onClose={(): void => {
              setIsDetailDialogOpen(false);
            }}
            open={isDetailDialogOpen}
          />
        </GalleryContextProvider>
        <SubstrateReuseDialog
          action="USE"
          callback={callbackUpdate}
          onClose={(): void => setIsReuseDialogOpen(false)}
          open={isReuseDialogOpen}
        />
        <SubstrateReuseDialog
          action="DUPLICATE"
          callback={callbackUpdate}
          onClose={(): void => setIsCloneDialogOpen(false)}
          open={isCloneDialogOpen}
        />
        <ParameterListDialog {...parameterListDialog} />
        <StyledMenu id="customized-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
          <StyledMenuItem
            onClick={(): void => {
              updateSubstrateContext(["TESA_STANDARD"]);
              updateSubstrateClasses(() => []);
              setPreSelectedProductType(null);
              handleClose();
              return setIsAddDialogOpen(true);
            }}
          >
            <ListItemText primary="tesa Standard" />
          </StyledMenuItem>
          <StyledMenuItem
            onClick={(): void => {
              updateSubstrateContext(["CUSTOMER_STANDARD", "NON_STANDARD"]);
              updateSubstrateClasses(() => []);
              setPreSelectedProductType(null);
              handleClose();
              return setIsAddDialogOpen(true);
            }}
          >
            <ListItemText primary="Customer Standard + Non-Standards" />
          </StyledMenuItem>
          <StyledMenuItem
            onClick={(): void => {
              updateSubstrateContext(["CUSTOMER_PART"]);
              updateSubstrateClasses(() => []);
              setPreSelectedProductType(null);
              handleClose();
              return setIsAddDialogOpen(true);
            }}
          >
            <ListItemText primary="Customer Parts" />
          </StyledMenuItem>
          <StyledMenuItem
            onClick={(): void => {
              handleClose();
              return setIsReuseDialogOpen(true);
            }}
          >
            <ListItemText primary="Use Existing Substrate Sample" />
          </StyledMenuItem>
          <StyledMenuItem
            onClick={(): void => {
              handleClose();
              return setIsCloneDialogOpen(true);
            }}
          >
            <ListItemText primary="Duplicate Existing Substrate Sample" />
          </StyledMenuItem>
        </StyledMenu>
        <CustomFooterGraphic />
        {sampleDetailsLoading && <CustomProgress variant="viewportCentered" />}
      </Box>
    </animated.div>
  );
};

export default SubstrateTab;
